import { useCreateTag, useGetTags } from '@shared/api/hooks/reports/tag';
import { FormProps } from '@shared/components/form/types';
import { MultiSelect } from '@shared/components/multi-select';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@shared/components/ui/form';
import { useToast } from '@shared/components/ui/use-toast';
import { cn } from '@shared/lib/utils';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

interface MultiTagsPickerProps extends FormProps {
  allowCreate?: boolean;
  disabledPlaceholder?: string;
  testId?: string;
}

function MultiTagsPicker({
  name,
  className,
  label,
  includeErrorMessage,
  allowCreate,
  placeholder = '',
  disabledPlaceholder = '',
  testId,
}: MultiTagsPickerProps) {
  const { control, setValue, watch } = useFormContext();
  const { toast } = useToast();

  const { data: tags, isLoading: isTagsLoading } = useGetTags();
  const { mutateAsync: createTag } = useCreateTag();

  const formattedTags = useMemo(() => {
    if (!tags) return [];
    return tags.map(({ uuid, name }) => ({
      value: uuid,
      label: name,
    }));
  }, [tags]);

  const selectedTags = watch(name);

  const onCreate = useCallback(
    async (tagName: string) => {
      try {
        const newTag = await createTag({ data: { name: tagName } });
        const newSelectedTags = [...(selectedTags || []), newTag.uuid];
        setValue(name, newSelectedTags, { shouldDirty: true });
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Failed to create tag',
          description: 'Please refresh and try again',
        });
      }
    },
    [createTag, name, selectedTags, setValue, toast]
  );

  const disabled =
    !formattedTags || (formattedTags.length === 0 && !allowCreate);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        return (
          <FormItem className={cn('grid flex-1 items-center', className)}>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <MultiSelect
                onCreate={allowCreate ? onCreate : undefined}
                itemName="tag"
                isLoading={isTagsLoading}
                options={formattedTags!}
                onValueChange={field.onChange}
                defaultValue={field.value}
                placeholder={disabled ? disabledPlaceholder : placeholder}
                disabled={disabled}
                className=" bg-gray-00"
                autoSize
                responsive
                maxWidth="100%"
                hideSelectAll
                modalPopover={true}
                data-testid={testId}
              />
            </FormControl>
            {includeErrorMessage && <FormMessage />}
          </FormItem>
        );
      }}
    />
  );
}

export default MultiTagsPicker;
